import styles from './CardGridModule.module.scss';

import { CardLayoutType, Theme } from 'api/contentful/contentTypes.cloverdotcom';
import { CardGridModuleCarousel } from 'components/CardGridModule/CardGridModuleCarousel/CardGridModuleCarousel';
import { useBreakpoints } from 'hooks/useBreakpoints';
import React, { ReactHTML } from 'react';
import type { AppType } from 'services/cms/typesUtil';
import { Grid } from '../Grid/Grid';
import { TextV2 } from '../TextV2/TextV2';
import { SingleCard } from './SingleCard/SingleCard';
import { CARD_GRID_MODULE_TEST_ID } from './CardGridModule.const';

type CardCridModuleProps = AppType.Component<{
  headline: string;
  subhead: string;
  items: AppType.Content[];
  cardLayout: string;
  themeStyle: string;
  headlineAlignment: string;
  headingTagName: keyof ReactHTML;
}>;

export const CardGridModule: React.FC<CardCridModuleProps> = ({
  headline,
  subhead,
  items,
  themeStyle = Theme.WHITE,
  headlineAlignment,
  cardLayout = CardLayoutType.GRID,
  headingTagName = 'h2',
  ...props
}) => {
  const { browser } = useBreakpoints();
  const isDesktopOrLarge = browser.is.desktop;

  const getHeadline = () => (
    <TextV2
      value={headline}
      variant="h2-medium"
      tagName={headingTagName}
      className={styles.cardModuleHeadline}
    />
  );

  const getSubhead = () => (
    <TextV2 value={subhead} variant="body" tagName="p" className={styles.cardModuleSubhead} />
  );

  const header = () => (
    <Grid item lg={10} md={9} sm={10} className={styles.cardModuleTitleContainer}>
      {headline && getHeadline()}
      {subhead && getSubhead()}
    </Grid>
  );

  const leftHeader = () => (
    <Grid lg={11} justifyContent="space-evenly" className={styles.cardModuleLeftHeader}>
      <Grid item lg={5} md={9} sm={10}>
        {headline && getHeadline()}
      </Grid>
      <Grid item lg={5} md={9} sm={10}>
        {subhead && getSubhead()}
      </Grid>
    </Grid>
  );

  const renderGrid = () => (
    <Grid container justifyContent="center" className={styles.cardModuleCards}>
      {items?.map(
        item =>
          item && (
            <Grid
              item
              key={item._sys.id}
              lg={5}
              md={9}
              sm={12}
              justifyContent="center"
              className={styles.cardModuleGridContainer}
              data-testid={CARD_GRID_MODULE_TEST_ID.singleCard}
            >
              <SingleCard item={item} theme={themeStyle} />
            </Grid>
          ),
      )}
    </Grid>
  );

  const renderCarousel = () => (
    <Grid container justifyContent="center" className={styles.cardModuleCards}>
      <CardGridModuleCarousel
        items={items}
        theme={themeStyle}
        data-testid={CARD_GRID_MODULE_TEST_ID.carouselLayout}
      />
    </Grid>
  );

  return (
    <section {...props._attributes} data-theme={themeStyle}>
      <Grid container className={styles.cardModule} justifyContent="center">
        {headlineAlignment === 'Left' && isDesktopOrLarge ? leftHeader() : header()}
        {cardLayout === CardLayoutType.CAROUSEL && items ? renderCarousel() : renderGrid()}
      </Grid>
    </section>
  );
};
